import { useCallback, useEffect, useState } from 'react';
import { errorMessage } from '../api/client';
import {
  amount,
  discountPercentOf,
  posApi,
  shelfStockOf,
  type StoreStock,
  type StoreStockBook,
} from '../api/pos';
import { money } from '../format';

const PAGE_SIZE = 50;

/** Cover thumbnail, falling back to the title's initial when there is no image. */
function CoverIcon({ book }: { book: StoreStockBook }) {
  const [failed, setFailed] = useState(false);
  const showImage = book.coverImageUrl && !failed;

  return showImage ? (
    <img
      className="shelf-cover"
      src={book.coverImageUrl as string}
      alt=""
      loading="lazy"
      onError={() => setFailed(true)}
    />
  ) : (
    <span className="shelf-cover shelf-cover-empty" aria-hidden="true">
      {book.title.charAt(0).toUpperCase()}
    </span>
  );
}

/**
 * What the shop has.
 *
 * One stock figure, shared with the website: every copy counted here can be
 * sold at this counter, and the website is selling the same copies at the same
 * time — so this screen goes stale the moment somebody checks out online.
 * There is no second "in the back" number any more, because there is no back.
 *
 * Sold-out titles are hidden by default, since the usual question is "what can
 * I sell". The toggle brings them back, greyed, so staff can answer "we had
 * that, we are out" instead of the book seeming never to have existed.
 */
export default function StoreStockScreen({
  onEdit,
}: {
  /** Open this book on the Add / edit book tab. */
  onEdit: (bookId: string) => void;
}) {
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [includeOutOfStock, setIncludeOutOfStock] = useState(false);
  const [page, setPage] = useState(1);

  const [data, setData] = useState<StoreStock | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const t = window.setTimeout(() => {
      setDebounced(search);
      setPage(1);
    }, 300);
    return () => window.clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [includeOutOfStock]);

  const load = useCallback(
    (signal?: AbortSignal) => {
      setLoading(true);
      posApi
        .storeStock(
          { search: debounced, includeOutOfStock, page, limit: PAGE_SIZE },
          signal,
        )
        .then((res) => {
          setData(res);
          setError(null);
        })
        .catch((err: unknown) => {
          if (signal?.aborted) return;
          setError(errorMessage(err, 'Could not load the shop stock.'));
        })
        .finally(() => {
          if (!signal?.aborted) setLoading(false);
        });
    },
    [debounced, includeOutOfStock, page],
  );

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const books = data?.books ?? [];
  const summary = data?.summary;
  const totalPages = data?.totalPages ?? 1;

  return (
    <div className="shelf">
      <div className="filters">
        <div className="filter-row">
          <label className="field field-grow">
            <span>Search</span>
            <input
              type="search"
              value={search}
              placeholder="Title, publisher, ISBN or SKU"
              onChange={(e) => setSearch(e.target.value)}
            />
          </label>
          <label className="field field-check">
            <input
              type="checkbox"
              checked={includeOutOfStock}
              onChange={(e) => setIncludeOutOfStock(e.target.checked)}
            />
            <span>Show sold-out titles</span>
          </label>
        </div>
      </div>

      {summary && (
        <div className="cashup">
          {/* Both figures count only what is in stock, whether or not
              sold-out titles are being shown — a headline that moved when you
              ticked a box would be read as stock appearing from nowhere. */}
          <div>
            <span className="cashup-label">Titles in stock</span>
            <strong>{summary.titlesInStore}</strong>
          </div>
          <div className="cashup-gross">
            <span className="cashup-label">Copies in stock</span>
            <strong>{summary.copiesInStore}</strong>
          </div>
        </div>
      )}

      {error && (
        <div>
          <p className="error">{error}</p>
          <button type="button" className="btn" onClick={() => load()}>
            Try again
          </button>
        </div>
      )}

      {loading ? (
        <p className="muted">Loading shop stock…</p>
      ) : books.length === 0 ? (
        <p className="muted empty">
          {debounced
            ? `Nothing on the shelf matches “${debounced}”.`
            : includeOutOfStock
              ? 'No books in the catalogue yet.'
              : 'Nothing in stock. Tick “Show sold-out titles” to see what the shop carries when it is back in.'}
        </p>
      ) : (
        <>
          <ul className="shelf-list">
            {books.map((book) => {
              const stock = shelfStockOf(book);
              const mrp = amount(book.mrp);
              const price = amount(book.price);
              const off = discountPercentOf(book);

              return (
                <li key={book.id} className={stock > 0 ? 'shelf-row' : 'shelf-row shelf-out'}>
                  <CoverIcon book={book} />

                  <div className="shelf-main">
                    <span className="shelf-title">{book.title}</span>
                    <span className="shelf-code">{book.sku ?? book.isbn13 ?? ''}</span>
                    {/* Which section to walk to. "Where is this one kept" is
                        the question the floor actually gets asked, and a title
                        on its own does not answer it. A book can sit in more
                        than one section, and plenty sit in none — so this is
                        absent rather than blank when there is nothing to say. */}
                    {!!book.categories?.length && (
                      <span className="shelf-cats">
                        {book.categories.map((name) => (
                          <span key={name} className="shelf-cat">
                            {name}
                          </span>
                        ))}
                      </span>
                    )}
                  </div>

                  <div className="shelf-price">
                    {/* MRP struck through only when the shop actually sells
                        below it — a crossed-out identical number reads as a
                        mistake. */}
                    {off > 0 && <span className="shelf-mrp">{money(mrp)}</span>}
                    <span className="shelf-sell">{money(price)}</span>
                    {off > 0 && <span className="shelf-off">{off}% off</span>}
                  </div>

                  <div className="shelf-stock">
                    {stock > 0 ? (
                      <span className="stock-count">{stock} in stock</span>
                    ) : (
                      // Said plainly, because it is the whole answer now.
                      // There is no warehouse line underneath offering hope.
                      <span className="stock-none">Sold out</span>
                    )}
                    <button
                      type="button"
                      className="link-btn shelf-edit"
                      onClick={() => onEdit(book.id)}
                      aria-label={`Edit ${book.title}`}
                    >
                      Edit
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>

          {totalPages > 1 && (
            <div className="pager">
              <button
                type="button"
                className="btn"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Previous
              </button>
              <span className="muted">
                Page {page} of {totalPages} · {data?.total ?? 0} titles
              </span>
              <button
                type="button"
                className="btn"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
